import create from 'zustand';

export type UploadReelFormData = {
  title: string;
  description: string;
  category: string;
  tags: string;
};

export type UploadVideoFormData = {
  title: string;
  description: string;
  genreIds: string[];
  ageRatingId: string;
  tags: string;
};

export type AdditionalImage = {
  file: File;
  thumbnailUrl: string;
  labelId: string;
  labelName: string;
  labelValue: string;
};

interface UploadState {
  file: File | null;
  thumbnail: File | null;
  thumbnailUrl: string | null;
  
  // New Video specific fields
  motionThumbnail: File | null;
  motionThumbnailUrl: string | null;
  staticThumbnail: File | null;
  staticThumbnailUrl: string | null;
  additionalImages: AdditionalImage[];
  
  previewUrl: string | null;
  values: UploadReelFormData;
  videoValues: UploadVideoFormData;
  returnPath: string | null;
  mediaId: string | null;
  duration: number | null;
  isInitiated: boolean;
  originalAdditionalImages: AdditionalImage[] | null;
  
  setFile: (file: File | null) => void;
  setThumbnail: (thumbnail: File | null, url: string | null) => void;
  setMotionThumbnail: (file: File | null, url: string | null) => void;
  setStaticThumbnail: (file: File | null, url: string | null) => void;
  setAdditionalImages: (images: AdditionalImage[]) => void;
  addAdditionalImage: (image: AdditionalImage) => void;
  removeAdditionalImage: (index: number) => void;
  
  setPreviewUrl: (url: string | null) => void;
  setValues: (values: Partial<UploadReelFormData>) => void;
  setVideoValues: (values: Partial<UploadVideoFormData>) => void;
  setReturnPath: (path: string | null) => void;
  setMediaId: (id: string | null) => void;
  setDuration: (duration: number | null) => void;
  setInitiated: (isInitiated: boolean) => void;
  setOriginalAdditionalImages: (images: AdditionalImage[] | null) => void;
  reset: () => void;
}

const initialReelValues: UploadReelFormData = {
  title: '',
  description: '',
  category: '',
  tags: '',
};

const initialVideoValues: UploadVideoFormData = {
  title: '',
  description: '',
  genreIds: [],
  ageRatingId: '',
  tags: '',
};

export const useUploadStore = create<UploadState>((set) => ({
  file: null,
  thumbnail: null,
  thumbnailUrl: null,
  
  motionThumbnail: null,
  motionThumbnailUrl: null,
  staticThumbnail: null,
  staticThumbnailUrl: null,
  additionalImages: [],
  
  previewUrl: null,
  values: initialReelValues,
  videoValues: initialVideoValues,
  returnPath: null,
  mediaId: null,
  duration: null,
  isInitiated: false,
  originalAdditionalImages: null,
  setFile: (file) => set({ file }),
  setThumbnail: (thumbnail, thumbnailUrl) => set((state) => {
    if (state.thumbnailUrl && state.thumbnailUrl.startsWith('blob:') && globalThis.window !== undefined) {
      URL.revokeObjectURL(state.thumbnailUrl);
    }
    return { thumbnail, thumbnailUrl };
  }),
  setMotionThumbnail: (motionThumbnail, motionThumbnailUrl) => set((state) => {
    if (state.motionThumbnailUrl && state.motionThumbnailUrl.startsWith('blob:') && globalThis.window !== undefined) {
      URL.revokeObjectURL(state.motionThumbnailUrl);
    }
    return { motionThumbnail, motionThumbnailUrl };
  }),
  setStaticThumbnail: (staticThumbnail, staticThumbnailUrl) => set((state) => {
    if (state.staticThumbnailUrl && state.staticThumbnailUrl.startsWith('blob:') && globalThis.window !== undefined) {
      URL.revokeObjectURL(state.staticThumbnailUrl);
    }
    return { staticThumbnail, staticThumbnailUrl };
  }),
  setAdditionalImages: (additionalImages) => set({ additionalImages }),
  addAdditionalImage: (image) => set((state) => ({ additionalImages: [...state.additionalImages, image] })),
  removeAdditionalImage: (index) => set((state) => {
    const newImages = [...state.additionalImages];
    const removed = newImages.splice(index, 1)[0];
    if (removed?.thumbnailUrl && removed.thumbnailUrl.startsWith('blob:') && globalThis.window !== undefined) {
      URL.revokeObjectURL(removed.thumbnailUrl);
    }
    return { additionalImages: newImages };
  }),
  setPreviewUrl: (previewUrl) => set({ previewUrl }),
  setValues: (newValues) => set((state) => ({ values: { ...state.values, ...newValues } })),
  setVideoValues: (newVideoValues) => set((state) => ({ videoValues: { ...state.videoValues, ...newVideoValues } })),
  setReturnPath: (returnPath) => set({ returnPath }),
  setMediaId: (mediaId) => set({ mediaId }),
  setDuration: (duration) => set({ duration }),
  setInitiated: (isInitiated) => set({ isInitiated }),
  setOriginalAdditionalImages: (originalAdditionalImages) => set({ originalAdditionalImages }),
  reset: () => set((state) => {
    // Revoke all blob URLs
    [state.thumbnailUrl, state.motionThumbnailUrl, state.staticThumbnailUrl, ...state.additionalImages.map(img => img.thumbnailUrl)]
      .forEach(url => {
        if (url && url.startsWith('blob:') && globalThis.window !== undefined) {
          URL.revokeObjectURL(url);
        }
      });

    return { 
      file: null, 
      thumbnail: null, 
      thumbnailUrl: null, 
      motionThumbnail: null,
      motionThumbnailUrl: null,
      staticThumbnail: null,
      staticThumbnailUrl: null,
      additionalImages: [],
      previewUrl: null, 
      values: initialReelValues, 
      videoValues: initialVideoValues,
      returnPath: null, 
      mediaId: null, 
      duration: null,
      isInitiated: false,
      originalAdditionalImages: null
    };
  })
}));
